<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-table-filter :filter-title="$t('expensa.menu_audit')">
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.eventType"
              :label="$t('expensa.audit_event_type')"
              :rules="rules.eventType"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.entityType"
              :label="$t('expensa.audit_entity_type')"
              :rules="rules.entityType"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.entityId"
              :label="$t('expensa.audit_entity_id')"
              :rules="rules.id"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.userId"
              :label="$t('expensa.audit_user_id')"
              :rules="rules.id"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="filters.fromDate"
              :label="$t('general.from_date')"
              :rules="rules.fromDate"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.toDate"
              :label="$t('general.to_date')"
              :rules="rules.toDate"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          @click="onClickReset"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <table-header :total="total" :loading="isLoading" />
    <oxd-text
      v-if="state === 'error'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.list_error') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'empty'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.audit_no_events') }}
    </oxd-text>
    <div v-else class="orangehrm-container">
      <oxd-card-table
        :items="items.data || []"
        :headers="headers"
        :selectable="false"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
  </div>
</template>

<script>
import {ref, computed, markRaw} from 'vue';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useDateFormat from '@/core/util/composable/useDateFormat';
import usei18n from '@/core/util/composable/usei18n';
import {
  digitsOnly,
  endDateShouldBeAfterStartDate,
  shouldNotExceedCharLength,
  validDateFormat,
} from '@/core/util/validation/rules';
import AuditData from '@/orangehrmExpensaPlugin/components/AuditData.vue';
import AuditEntity from '@/orangehrmExpensaPlugin/components/AuditEntity.vue';
import {expensaApi} from '../util/api';
import {instant, personName} from '../util/format';
import {listState} from '../util/listState';

const defaultFilters = () => ({
  eventType: '',
  entityType: '',
  entityId: '',
  userId: '',
  fromDate: null,
  toDate: null,
});

const text = (v) => (v ? String(v).trim() || null : null);

export default {
  name: 'ExpensaAudit',
  setup() {
    const {$t} = usei18n();
    const {userDateFormat} = useDateFormat();
    const filters = ref(defaultFilters());

    const serializedFilters = computed(() => ({
      eventType: text(filters.value.eventType),
      entityType: text(filters.value.entityType),
      entityId: text(filters.value.entityId),
      userId: text(filters.value.userId),
      fromDate: filters.value.fromDate || null,
      toDate: filters.value.toDate || null,
    }));

    const normalizer = (data) =>
      data.map((e) => ({
        id: e.id,
        createdAt: instant(e.createdAt),
        user: e.user ? personName(e.user) : $t('expensa.audit_system'),
        eventType: e.eventType,
        entityType: e.entityType,
        entityId: e.entityId,
        data: e.data,
      }));

    const http = expensaApi('/audit');
    // usePaginate invia sempre limit (50): limit=0 sarebbe "tutto" lato server.
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer,
      query: serializedFilters,
      toastNoRecords: false,
    });

    return {
      userDateFormat,
      filters,
      showPaginator,
      currentPage,
      total,
      pages,
      items: response,
      isLoading,
      execQuery,
    };
  },
  data() {
    return {
      headers: [
        {
          name: 'createdAt',
          title: this.$t('expensa.audit_when'),
          style: {flex: 2},
        },
        {name: 'user', title: this.$t('expensa.audit_user'), style: {flex: 2}},
        {
          name: 'eventType',
          title: this.$t('expensa.audit_event'),
          style: {flex: 2},
        },
        {
          name: 'entity',
          title: this.$t('expensa.audit_entity'),
          style: {flex: 2},
          cellRenderer: this.entityRenderer,
        },
        {
          name: 'data',
          title: this.$t('expensa.audit_data'),
          style: {flex: 5},
          cellRenderer: this.dataRenderer,
        },
      ],
    };
  },
  computed: {
    rules() {
      return {
        eventType: [shouldNotExceedCharLength(60)],
        entityType: [shouldNotExceedCharLength(40)],
        id: [digitsOnly, shouldNotExceedCharLength(10)],
        fromDate: [validDateFormat(this.userDateFormat)],
        toDate: [
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.filters.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
      };
    },
    state() {
      return listState(this.isLoading, this.items, this.total);
    },
  },
  methods: {
    entityRenderer(...[, , , row]) {
      return {
        component: markRaw(AuditEntity),
        props: {entityType: row.entityType, entityId: row.entityId},
      };
    },
    dataRenderer(...[, , , row]) {
      return {component: markRaw(AuditData), props: {data: row.data}};
    },
    async filterItems() {
      await this.execQuery();
    },
    onClickReset() {
      this.filters = defaultFilters();
      this.filterItems();
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
</style>
